/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Badge,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useProducts, useProduct} from '@salesforce/commerce-sdk-react'

const ProductSashes = (productData) => {
    const product = productData.product
    const shownInstock = productData?.shownInstock ?? false
    const type = product?.productType || product?.type
    const styles = useMultiStyleConfig('ProductSashes')
    const variationsIDS = type?.master
        ? product?.variants?.map((variant) => variant?.productId).join(',') ?? ''
        : ''
    const isProduct = type?.item || type?.option || type?.variant || type?.standard || false
    const isMasterProduct = type?.master || false
    const isProductBundle = type?.bundle || false
    const {data: masterResponse} = useProducts(
        {
            parameters: {
                ids: variationsIDS,
                perPricebook: true,
                allImages: true,
                expand: []
            }
        },
        {enabled: Boolean(isMasterProduct)}
    )
    const {data: productResponse} = useProduct(
        {
            parameters: {
                id: product?.productId || product?.id,
                perPricebook: true,
                allImages: true,
                expand: ['bundled_products', 'availability']
            }
        },
        {enabled: Boolean(isProduct || isProductBundle)}
    )
    const productDetails = type?.master ? masterResponse?.data : productResponse
    let bundleChildVariantIds = ''
    if (productDetails?.bundledProducts?.length > 0)
        bundleChildVariantIds = Object.keys(productDetails?.bundledProducts)
            ?.map((key) => {
                return productDetails?.bundledProducts?.[key]?.product?.id
            })
            .join(',')
    const {data: bundleChildrenData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds,
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory,master))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0
        }
    )
    const getAutoSash = (productDetails, sashes, isComingSoon, hideSashes) => {
        const availabilityModel = productDetails

        if (!availabilityModel || !availabilityModel?.inventory) {
            return sashes
        }

        const ATS = availabilityModel?.inventory?.ats
        const availabilityStatus = availabilityModel?.inventory

        try {
            if (availabilityStatus && availabilityModel?.inventory) {
                if (
                    availabilityStatus?.stockLevel > 0 &&
                    ATS > 0 &&
                    hideSashes != 'In-Stock-No' &&
                    shownInstock
                ) {
                    sashes.push({
                        name: 'instock',
                        msg: <FormattedMessage defaultMessage="In-Stock" id="label.sash.instock" />
                    })
                } else if (ATS == 0 && hideSashes != 'Sold-Out-No' && !isComingSoon) {
                    sashes.push({
                        name: 'soldout',
                        msg: <FormattedMessage defaultMessage="Sold-Out" id="label.sash.soldout" />
                    })
                } else if (
                    availabilityStatus?.backorderable &&
                    ATS > 0 &&
                    availabilityStatus?.stockLevel == 0
                ) {
                    // back-order sash
                    sashes.push({
                        name: 'backorder',
                        msg: <FormattedMessage defaultMessage="Back-Order" id="label.back.order" />
                    })
                } else if (
                    availabilityStatus.preorderable == true &&
                    ATS != 0 &&
                    hideSashes != 'Pre-Order-No'
                ) {
                    sashes.push({
                        name: 'preorder',
                        msg: (
                            <FormattedMessage defaultMessage="Pre-Order" id="label.sash.preorder" />
                        )
                    })
                } else if (hideSashes != 'Coming-Soon-No' && isComingSoon && ATS == 0) {
                    sashes.push({
                        name: 'comingsoon',
                        msg: (
                            <FormattedMessage
                                defaultMessage="Coming Soon"
                                id="label.sash.comingsoon"
                            />
                        )
                    })
                }
            }
        } catch (error) {
            console.error('getAutoSash: exception occurred = ' + error?.message)
        }

        return sashes
    }

    /**
     * sash status based on configuration at product level
     * @param {String} productSash - sash value from the product level attribute
     * @returns {Array} the product sash value.
     */

    const getSashBasedOnConfig = (productSash, sashes, productType) => {
        try {
            // for bundles coming soon will be decided without automation logic
            if (productType == 'bundle') {
                if (productSash == 'Coming-Soon-Yes') {
                    sashes.push({
                        name: 'comingsoon',
                        msg: <FormattedMessage defaultMessage="Coming Soon" id="Coming Soon" />
                    })
                }
            }

            if (productSash == 'In-Stock-Yes' && shownInstock) {
                sashes.push({
                    name: 'instock',
                    msg: <FormattedMessage defaultMessage="In-Stock" id="label.sash.instock" />
                })
            } else if (productSash == 'Sold-Out-Yes') {
                // out of stock sash
                sashes.push({
                    name: 'soldout',
                    msg: <FormattedMessage defaultMessage="Sold-Out" id="label.sash.soldout" />
                })
            } else if (productSash == 'Pre-Order-Yes') {
                // pre-order sash
                sashes.push({
                    name: 'preorder',
                    msg: <FormattedMessage defaultMessage="Pre-Order" id="label.sash.preorder" />
                })
            } else if (productSash == 'Back-Order-Yes') {
                // back-order sash
                sashes.push({
                    name: 'backorder',
                    msg: <FormattedMessage defaultMessage="Back-Order" id="label.back.order" />
                })
            }
        } catch (error) {
            console.error('getSashBasedOnConfig: exception occured = ' + error?.message)
        }
        return sashes
    }

    /**
     * sashes for Master, Product Bundle and product sets
     * @returns {Array} the product sash value.
     */
    const getSashForMasterAndOtherProducts = (
        instock,
        preOrder,
        soldout,
        backorder,
        sashes,
        listofVariations,
        hideSashes,
        isBundle,
        isComingSoon,
        onlineCount
    ) => {
        if (isBundle) {
            if (soldout >= 1 && hideSashes != 'Sold-Out-No') {
                sashes.push({
                    name: 'soldout',
                    msg: <FormattedMessage defaultMessage="Sold-Out" id="label.sash.soldout" />
                })
            } else if (preOrder > 0 && instock > 0 && hideSashes != 'Pre-Order-No') {
                sashes.push({
                    name: 'preorder',
                    msg: <FormattedMessage defaultMessage="Pre-Order" id="label.sash.preorder" />
                })
            } else if (backorder > 0 && instock > 0) {
                sashes.push({
                    name: 'backorder',
                    msg: <FormattedMessage defaultMessage="Back-Order" id="label.back.order" />
                })
            } else if (backorder > 0 && preOrder > 0) {
                sashes.push({
                    name: 'preorder',
                    msg: <FormattedMessage defaultMessage="Pre-Order" id="label.sash.preorder" />
                })
            } else if (
                instock == listofVariations?.data?.length &&
                hideSashes != 'In-Stock-No' &&
                shownInstock
            ) {
                sashes.push({
                    name: 'instock',
                    msg: <FormattedMessage defaultMessage="In-Stock" id="label.sash.instock" />
                })
            } else if (backorder == listofVariations?.data?.length) {
                sashes.push({
                    name: 'backorder',
                    msg: <FormattedMessage defaultMessage="Back-Order" id="label.back.order" />
                })
            } else if (preOrder == listofVariations?.data?.length && hideSashes != 'Pre-Order-No') {
                sashes.push({
                    name: 'preorder',
                    msg: <FormattedMessage defaultMessage="Pre-Order" id="label.sash.preorder" />
                })
            }
        } else {
            if (instock >= 1 && hideSashes != 'In-Stock-No' && shownInstock) {
                sashes.push({
                    name: 'instock',
                    msg: <FormattedMessage defaultMessage="In-Stock" id="label.sash.instock" />
                })
            } else if (
                soldout == listofVariations?.length &&
                hideSashes != 'Sold-Out-No' &&
                !isComingSoon
            ) {
                sashes.push({
                    name: 'soldout',
                    msg: <FormattedMessage defaultMessage="Sold-Out" id="label.sash.soldout" />
                })
            } else if (preOrder >= 1 && hideSashes != 'Pre-Order-No') {
                sashes.push({
                    name: 'preorder',
                    msg: <FormattedMessage defaultMessage="Pre-Order" id="label.sash.preorder" />
                })
            } else if (backorder > 0 && backorder == onlineCount) {
                sashes.push({
                    name: 'backorder',
                    msg: <FormattedMessage defaultMessage="Back-Order" id="label.back.order" />
                })
            } else if (
                soldout == listofVariations?.length &&
                isComingSoon &&
                hideSashes != 'Coming-Soon-No'
            ) {
                sashes.push({
                    name: 'comingsoon',
                    msg: (
                        <FormattedMessage defaultMessage="Coming Soon" id="label.sash.comingsoon" />
                    )
                })
            }
        }

        return sashes
    }

    //checking promotion level atribute enabled or not in multiple promotions
    const showSaleBadge = (product) => {
        var showSaleBadgeOnProduct = true
        if (product && product?.productPromotions) {
            var promotions = product?.productPromotions
            for (let promo of promotions) {
                if (
                    promo?.showSaleBadge ||
                    product?.representedProduct?.c_saleSash ||
                    product?.c_saleSash
                ) {
                    showSaleBadgeOnProduct = true
                    break
                } else {
                    showSaleBadgeOnProduct = false
                }
            }
        }
        return showSaleBadgeOnProduct
    }

    const isSalePrice = (product) => {
        let hasSale = false

        const listPrice =
            Array.isArray(product?.tieredPrices) && product?.tieredPrices?.length > 0
                ? product.tieredPrices[0]?.price
                : null

        const salePrice =
            Array.isArray(product?.tieredPrices) && product?.tieredPrices?.length > 1
                ? product.tieredPrices[1]?.price
                : null

        if (listPrice && salePrice && salePrice < listPrice) {
            hasSale = true
        }

        return hasSale
    }
    const isStandard =
        productDetails?.type?.item ||
        productDetails?.type?.option ||
        productDetails?.type?.variant ||
        productDetails?.type?.standard ||
        false
    const isMaster = type?.master
    const isBundle = productDetails?.type?.bundle
    let sashes = []
    let hideSashes
    let tileSashesConfig = true
    let ShowTileSashes = false

    const productSash = productDetails?.c_tileSashes
    const today = new Date() // Current date
    const streetDate =
        productDetails?.c_streetDate && productDetails?.c_streetDate.trim() !== ''
            ? new Date(productDetails?.c_streetDate)
            : today

    // Compare the dates
    const isComingSoon = today < streetDate
    if (productSash && typeof productSash !== 'undefined' && productSash?.includes('No')) {
        // Do not display the badge if configured as 'NO'
        hideSashes = productSash
        ShowTileSashes = true
    } else if (
        !isBundle &&
        productSash &&
        typeof productSash !== 'undefined' &&
        productSash?.includes('Yes')
    ) {
        // Return the respective sash based on configuration for non-bundle products
        sashes = getSashBasedOnConfig(productSash, sashes, type)
        tileSashesConfig = false
    } else if (isBundle && typeof productSash !== 'undefined' && productSash.includes('Yes')) {
        // Return the respective sash based on configuration for bundles
        sashes = getSashBasedOnConfig(productSash, sashes, type)
        tileSashesConfig = false
    }

    // Check automation conditions if product sash is not configured
    if (typeof productSash === 'undefined') {
        ShowTileSashes = true
    }
    if (ShowTileSashes && tileSashesConfig) {
        let instock = 0
        let preOrder = 0
        let soldout = 0
        let backorder = 0
        let onlineCount = 0
        if (isStandard) {
            sashes = getAutoSash(productDetails, sashes, isComingSoon, hideSashes)
        } else if (isMaster && productDetails) {
            const listofVariations = productDetails
            listofVariations?.map((variant) => {
                if (variant && variant?.inventory) {
                    if (variant?.inventory?.orderable) {
                        onlineCount += 1
                    }
                    if (variant?.inventory?.stockLevel > 0 && variant.inventory.ats > 0) {
                        instock += 1
                    } else if (variant.inventory.ats == 0) {
                        soldout += 1
                    } else if (variant.inventory.preorderable == true) {
                        preOrder += 1
                    } else if (variant.inventory.backorderable == true) {
                        backorder += 1
                    }
                } else if (!variant.inventory) {
                    soldout += 1
                }
            })
            var masterSash = getSashForMasterAndOtherProducts(
                instock,
                preOrder,
                soldout,
                backorder,
                sashes,
                listofVariations,
                hideSashes,
                isBundle,
                isComingSoon,
                onlineCount
            )
            sashes = masterSash
        } else if (isBundle) {
            bundleChildrenData?.data?.map((bundleProduct) => {
                if (bundleProduct && bundleProduct?.inventory) {
                    if (bundleProduct?.inventory?.orderable) {
                        onlineCount += 1
                    }
                    if (
                        bundleProduct?.inventory?.stockLevel > 0 &&
                        bundleProduct.inventory.ats > 0
                    ) {
                        instock += 1
                    } else if (bundleProduct.inventory.ats == 0) {
                        soldout += 1
                    } else if (bundleProduct.inventory.preorderable == true) {
                        preOrder += 1
                    } else if (bundleProduct.inventory.backorderable == true) {
                        backorder += 1
                    }
                } else if (!bundleProduct.inventory) {
                    soldout += 1
                }
            })
            var bundleSash = getSashForMasterAndOtherProducts(
                instock,
                preOrder,
                soldout,
                backorder,
                sashes,
                bundleChildrenData,
                hideSashes,
                isBundle,
                isComingSoon,
                onlineCount
            )
            sashes = bundleSash
        }
    }
    const isShowBadges = showSaleBadge(product)
    if (isShowBadges) {
        var saleSash = product?.representedProduct?.c_saleSash || product?.c_saleSash
        // sale sash, if it is set to No then dont display and if Yes no need to check automation condition
        if (saleSash !== 'undefined' && saleSash) {
            sashes.push({
                name: 'sale',
                msg: <FormattedMessage defaultMessage="SALE" id="label.sash.sale" />
            })
        } else if (saleSash === 'undefined' && isSalePrice(product)) {
            // if it is none then check automation condition
            sashes.push({
                name: 'sale',
                msg: <FormattedMessage defaultMessage="SALE" id="label.sash.sale" />
            })
        }

        //display sashes on master product if any one variant has sashes
        if (isMaster && !saleSash && !isSalePrice(product)) {
            const listofVariationsProducts = productDetails
            listofVariationsProducts?.map((variantProduct) => {
                if (variantProduct) {
                    var variantSaleSash =
                        variantProduct?.representedProduct?.c_saleSash || variantProduct?.c_saleSash
                    if (variantSaleSash !== 'undefined' && variantSaleSash) {
                        sashes.push({
                            name: 'sale',
                            msg: <FormattedMessage defaultMessage="SALE" id="label.sash.sale" />
                        })
                        return true
                    } else if (variantSaleSash === 'undefined' && isSalePrice(variantProduct)) {
                        sashes.push({
                            name: 'sale',
                            msg: <FormattedMessage defaultMessage="SALE" id="label.sash.sale" />
                        })
                        return true
                    }
                }
            })
        }
    }

    if (productDetails?.c_exclusiveSash && sashes.length < 2) {
        sashes.push({
            name: 'exclusive',
            msg: <FormattedMessage defaultMessage="EXCLUSIVE" id="label.sash.exclusive" />
        })
    }

    // limited availability message
    if (productDetails?.c_limitedAvailability && sashes.length < 2) {
        sashes.push({
            name: 'limitedStock',
            msg: (
                <FormattedMessage
                    defaultMessage="LIMITED TIME"
                    id="label.sash.limitedAvailability"
                />
            )
        })
    }

    const exclusiveCustomStyles = {
        '.Exclusive:before': {
            content: '"-"',
            display: 'block',
            backgroundImage: `url(${getAssetUrl('static/img/cr-mark.svg')})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '0px center',
            backgroundSize: {base: '9px 9px', lg: '16px 16px'},
            float: 'left',
            width: {base: '9px', lg: '16px'},
            mr: '2px'
        }
    }

    return (
        <Box className="productSashesContent">
            {sashes?.map((sash, index) => (
                <Badge key={index} {...{...styles[sash.name], ...styles.sashblock}}>
                    <Box className={`sash ${sash.name}`}>
                        {sash.msg.props.defaultMessage[0].value === 'In-Stock' && shownInstock && (
                            <Box className="Instock">{sash.msg.props.defaultMessage[0].value}</Box>
                        )}
                        {sash.msg.props.defaultMessage[0].value === 'Pre-Order' && (
                            <Box className="Pre-Order">
                                {sash.msg.props.defaultMessage[0].value}
                            </Box>
                        )}
                        {sash.msg.props.defaultMessage[0].value === 'Back-Order' && (
                            <Box className="Backorder">
                                {sash.msg.props.defaultMessage[0].value}
                            </Box>
                        )}
                        {sash.msg.props.defaultMessage[0].value === 'Sold-Out' && (
                            <Box className="Sold Out">{sash.msg.props.defaultMessage[0].value}</Box>
                        )}
                        {sash.msg.props.defaultMessage[0].value === 'EXCLUSIVE' && (
                            <Box sx={exclusiveCustomStyles}>
                                <Box
                                    className="Exclusive"
                                    textAlign={'center'}
                                    display={'flex'}
                                    alignItems={'center'}
                                >
                                    {sash.msg.props.defaultMessage[0].value}
                                </Box>
                            </Box>
                        )}
                        {sash.msg.props.defaultMessage[0].value === 'Coming Soon' && (
                            <Box className="coming-soon" color={'woodsmoke'}>
                                {sash.msg.props.defaultMessage[0].value}
                            </Box>
                        )}
                        {sash.msg.props.defaultMessage[0].value === 'SALE' && (
                            <Box className="sale">{sash.msg.props.defaultMessage[0].value}</Box>
                        )}
                        {sash.msg.props.defaultMessage[0].value === 'LIMITED TIME' && (
                            <Box className="LIMITED TIME">
                                {sash.msg.props.defaultMessage[0].value}
                            </Box>
                        )}
                    </Box>
                </Badge>
            ))}
        </Box>
    )
}

ProductSashes.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    productData: PropTypes.shape({
        currency: PropTypes.string,
        c_customerGroups: PropTypes.array,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        imageGroups: PropTypes.array,
        price: PropTypes.number,
        priceRanges: PropTypes.array,
        tieredPrices: PropTypes.array,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        productId: PropTypes.string,
        productPromotions: PropTypes.array,
        productType: PropTypes.object,
        representedProduct: PropTypes.object,
        hitType: PropTypes.string,
        variationAttributes: PropTypes.array,
        variants: PropTypes.array,
        type: PropTypes.shape({
            set: PropTypes.bool,

            bundle: PropTypes.bool,
            item: PropTypes.bool
        })
    })
}

export default ProductSashes
